// Parsing and formatting for the time picker.
//
// Deliberately lenient about how a time is typed — on a building site people type "7", "730"
// or "7:5" far more often than "07:30" — but strict about what counts as a real time.

const SEPARATED = /^(\d{1,2})[:.,;](\d{1,2})$/
const DIGITS_ONLY = /^(\d{1,4})$/

function build(hours, minutes) {
  if (hours > 23 || minutes > 59) return null

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`
}

// "7" -> 07:00, "730" -> 07:30, "1945" -> 19:45, "7:5" -> 07:05, "7.30" -> 07:30.
// Returns null for anything that is not a real time.
export function parseTime(value) {
  if (typeof value !== "string") return null

  const trimmed = value.trim()
  if (trimmed === "") return null

  const separated = trimmed.match(SEPARATED)
  if (separated) return build(Number(separated[1]), Number(separated[2]))

  const digits = trimmed.match(DIGITS_ONLY)
  if (!digits) return null

  const raw = digits[1]
  // 1 or 2 digits is an hour; 3 or 4 digits is hour+minutes.
  if (raw.length <= 2) return build(Number(raw), 0)

  return build(Number(raw.slice(0, raw.length - 2)), Number(raw.slice(-2)))
}

// Every selectable time of day at `step` minutes, as "HH:MM".
export function timesOfDay(step) {
  const minutes = Number(step) > 0 ? Number(step) : 15
  const options = []

  for (let elapsed = 0; elapsed < 24 * 60; elapsed += minutes) {
    options.push(build(Math.floor(elapsed / 60), elapsed % 60))
  }

  return options
}

// Snaps a valid time onto the nearest step, which is what the flatpickr timepicker did on
// blur. Times that are already on a step are returned unchanged.
export function snapTime(value, step) {
  const parsed = parseTime(value)
  const minutes = Number(step) > 0 ? Number(step) : null
  if (!parsed || !minutes) return parsed

  const [hours, mins] = parsed.split(":").map(Number)
  const total = hours * 60 + mins
  const snapped = Math.round(total / minutes) * minutes
  // Snapping 23:59 upwards must not roll into the next day.
  const clamped = Math.min(snapped, 23 * 60 + 59)

  return build(Math.floor(clamped / 60), clamped % 60)
}
